import React, { useState } from 'react';
import { Send, Mail, User, MessageSquare, CheckCircle } from 'lucide-react';
import { contactAPI, newsletterAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    // Simulate submission
    setTimeout(() => {
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    }, 500);
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.newsletter.value;
    if (email) {
      toast.success('Thanks for subscribing! You\'re now part of the Thinkkool family!');
      e.target.reset();
    } else {
      toast.error('Please enter a valid email address');
    }
  };

  return (
    <section id="contact" className="py-20 px-[5%]" style={{ background: 'var(--light)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block font-extrabold text-xs tracking-[2px] uppercase mb-[10px]"
            style={{ color: 'var(--pink-d)' }}
          >
            Contact
          </span>
          <h2
            className="fredoka mb-2"
            style={{
              fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
              color: 'var(--dark)',
            }}
          >
            Get in Touch 📮
          </h2>
          <p
            className="text-base leading-relaxed max-w-2xl mx-auto"
            style={{ color: 'var(--mid)' }}
          >
            Have questions? Want to know more about our products? We'd love to hear from you!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-2 border-[#61525a]/20 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#1e1919] mb-6">
                Send us a Message
              </h3>

              {isSubmitted ? (
                <div className="py-12 text-center space-y-4 animate-in fade-in zoom-in duration-300">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-[#1e1919]">
                    Message Sent!
                  </h4>
                  <p className="text-[#736c64]">
                    Thank you for reaching out. We'll respond within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-[#1e1919] flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>Your Name</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="border-[#61525a]/30 focus:border-[#61525a] transition-colors duration-200"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-[#1e1919] flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <span>Your Email</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="border-[#61525a]/30 focus:border-[#61525a] transition-colors duration-200"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-[#1e1919] flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4" />
                      <span>Your Message</span>
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us what you're looking for..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="border-[#61525a]/30 focus:border-[#61525a] transition-colors duration-200 resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#61525a] hover:bg-[#4a3f45] text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Newsletter & Info */}
          <div className="space-y-8">
            {/* Newsletter Card */}
            <Card className="border-2 border-[#61525a]/20 shadow-xl overflow-hidden">
              <CardContent className="p-8 bg-gradient-to-br from-[#fad24b]/20 to-[#ff8c19]/20">
                <h3 className="text-2xl font-bold text-[#1e1919] mb-4">
                  Join Our Newsletter
                </h3>
                <p className="text-[#736c64] mb-6">
                  Get exclusive deals, new product alerts, and fun surprises delivered to your inbox!
                </p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <Input
                    type="email"
                    name="newsletter"
                    placeholder="Enter your email"
                    className="border-[#61525a]/30 focus:border-[#61525a] bg-white transition-colors duration-200"
                    required
                  />
                  <Button
                    type="submit"
                    className="w-full bg-[#61525a] hover:bg-[#4a3f45] text-white py-6 rounded-xl transition-all duration-300"
                  >
                    Subscribe Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="border-2 border-[#61525a]/20 shadow-xl">
              <CardContent className="p-8 space-y-6">
                <h3 className="text-2xl font-bold text-[#1e1919]">
                  Contact Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#3dd3ee]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#3dd3ee]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1e1919]">Email</div>
                      <div className="text-[#736c64]">sales@thinkooltoys.com</div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#fad24b]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-[#fad24b]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1e1919]">Business Hours</div>
                      <div className="text-[#736c64]">Mon-Fri: 9AM - 6PM EST</div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#61525a]/20">
                  <p className="text-sm text-[#736c64]">
                    We typically respond within 24 hours. For urgent inquiries, please mention it in your message!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

